export const reportTheme = {
    colour: {
        ink: "#233f32",
        muted: "#586b5d",
        line: "#d6dbcf",
        paper: "#f3f0e7",
        wash: "#f3f0e7",
        brand: "#214e3a",
        brandDark: "#17372b",
        pass: "#214e3a",
        warn: "#9b5d00",
        fail: "#a9342f",
    },
    spacingMm: {
        pageHorizontal: 14,
        pageTop: 17,
        pageBottom: 18,
    },
};
export const reportStyles = `
:root {
  --sp-report-ink: ${reportTheme.colour.ink};
  --sp-report-muted: ${reportTheme.colour.muted};
  --sp-report-line: ${reportTheme.colour.line};
  --sp-report-paper: ${reportTheme.colour.paper};
  --sp-report-wash: ${reportTheme.colour.wash};
  --sp-report-brand: ${reportTheme.colour.brand};
  --sp-report-brand-dark: ${reportTheme.colour.brandDark};
}

* { box-sizing: border-box; }

html,
body {
  background: var(--sp-report-paper);
  margin: 0;
}

.sp-report {
  color: var(--sp-report-ink);
  background: var(--sp-report-paper);
  font-family: Inter, "Helvetica Neue", Arial, sans-serif;
  font-size: 9.5pt;
  line-height: 1.42;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.sp-report__page-header,
.sp-report__page-footer {
  color: var(--sp-report-muted);
  font-size: 7.5pt;
  left: 0;
  position: fixed;
  right: 0;
}

.sp-report__page-header {
  border-bottom: 0.2mm solid var(--sp-report-line);
  padding-bottom: 2mm;
  top: -11mm;
}

.sp-report__page-footer {
  border-top: 0.2mm solid var(--sp-report-line);
  bottom: 0;
  display: flex;
  justify-content: space-between;
  padding-top: 2mm;
}

.sp-section {
  break-before: page;
  padding: 0;
}

.sp-section:first-of-type { break-before: auto; }

.sp-section__eyebrow {
  color: var(--sp-report-brand);
  font-size: 8pt;
  font-weight: 750;
  letter-spacing: 0.08em;
  margin: 0 0 2mm;
  text-transform: uppercase;
}

.sp-section h1,
.sp-section h2,
.sp-section h3 {
  color: var(--sp-report-brand-dark);
  font-weight: 620;
  letter-spacing: -0.018em;
  margin-top: 0;
}

.sp-section h1 { font-size: 26pt; line-height: 1.08; }
.sp-section h2 { font-size: 18pt; line-height: 1.15; margin-bottom: 5mm; }
.sp-section h3 { font-size: 11pt; margin-bottom: 2mm; margin-top: 6mm; }

.sp-cover {
  align-content: space-between;
  background: var(--sp-report-paper);
  border-top: 3mm solid var(--sp-report-brand);
  display: grid;
  min-height: 245mm;
  padding: 13mm 12mm 12mm;
}

.sp-cover__mark {
  color: var(--sp-report-brand);
  font-size: 12pt;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.sp-cover__meta {
  border-top: 0.4mm solid var(--sp-report-brand);
  display: grid;
  gap: 2mm;
  padding-top: 4mm;
}

.sp-cover__meta strong { font-size: 11pt; }

.sp-summary-grid {
  display: grid;
  gap: 3mm;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.sp-summary-card {
  background: transparent;
  border: 0.2mm solid var(--sp-report-line);
  border-radius: 0.8mm;
  min-height: 19mm;
  padding: 3mm;
}

.sp-summary-card__label {
  color: var(--sp-report-muted);
  display: block;
  font-size: 7.5pt;
  font-weight: 600;
  letter-spacing: 0.04em;
  margin-bottom: 1mm;
  text-transform: uppercase;
}

.sp-report table {
  border-collapse: collapse;
  font-size: 8.25pt;
  margin: 2mm 0 5mm;
  width: 100%;
}

.sp-report thead { display: table-header-group; }
.sp-report tr { break-inside: avoid; }

.sp-report th,
.sp-report td {
  border-bottom: 0.2mm solid var(--sp-report-line);
  padding: 2mm 1.6mm;
  text-align: left;
  vertical-align: top;
}

.sp-report th {
  background: transparent;
  color: var(--sp-report-muted);
  font-size: 7.2pt;
  font-weight: 600;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.sp-list { margin: 2mm 0 5mm; padding-left: 5mm; }
.sp-list li { margin-bottom: 1.4mm; }
.sp-muted { color: var(--sp-report-muted); }

.sp-status {
  border-radius: 0.6mm;
  display: inline-block;
  font-size: 7pt;
  font-weight: 650;
  letter-spacing: 0.03em;
  padding: 0.6mm 1.7mm;
  text-transform: uppercase;
  white-space: nowrap;
}

.sp-status--pass { background: #e0eadf; color: ${reportTheme.colour.pass}; }
.sp-status--warn { background: #fff2d8; color: ${reportTheme.colour.warn}; }
.sp-status--fail { background: #fbe7e5; color: ${reportTheme.colour.fail}; }
.sp-status--not_applicable { background: #e9e9df; color: var(--sp-report-muted); }

.sp-finding {
  border-left: 0.8mm solid var(--sp-report-line);
  margin: 0 0 3mm;
  padding: 1mm 0 1mm 4mm;
}

.sp-finding--acknowledged { border-left-color: var(--sp-report-brand); }

.sp-photo-grid {
  display: grid;
  gap: 4mm;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.sp-photo { break-inside: avoid; margin: 0; }
.sp-photo img { display: block; height: 70mm; object-fit: cover; width: 100%; }
.sp-photo figcaption { color: var(--sp-report-muted); font-size: 8pt; padding-top: 1.5mm; }

@page {
  background: var(--sp-report-paper);
  size: A4;
  margin: ${reportTheme.spacingMm.pageTop}mm ${reportTheme.spacingMm.pageHorizontal}mm ${reportTheme.spacingMm.pageBottom}mm;
}

@media screen {
  body { background: #d6dbcf; }
  .sp-report { box-shadow: 0 4mm 12mm rgb(35 63 50 / 10%); margin: 8mm auto; max-width: 210mm; padding: 17mm 14mm 18mm; }
  .sp-report__page-header, .sp-report__page-footer { display: none; }
}

@media print {
  .sp-report { margin: 0; }
}
`;
export const reportWebStyles = `
.sp-report--web { color: var(--color-stone-950); background: transparent; font-family: "Inter Variable", Inter, sans-serif; font-size: 14px; line-height: 1.6; }
.sp-report--web .sp-report__page-header, .sp-report--web .sp-report__page-footer { display: none; }
.sp-report--web .sp-section { break-before: auto; }
.sp-report--web .sp-section__eyebrow { color: var(--color-emerald-800); font-size: 11px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase; margin: 0 0 8px; }
.sp-report--web h1, .sp-report--web h2, .sp-report--web h3 { color: var(--color-emerald-950); line-height: 1.25; }
.sp-report--web h2 { font-size: 24px; margin: 0 0 20px; }
.sp-report--web h3 { font-size: 17px; margin: 28px 0 10px; }
.sp-report--web p { margin: 8px 0; }
.sp-report--web table { border-collapse: collapse; width: 100%; min-width: 640px; margin: 10px 0 26px; font-size: 13px; }
.sp-report--web th, .sp-report--web td { border-bottom: 1px solid var(--color-stone-200); padding: 10px 9px; text-align: left; vertical-align: top; }
.sp-report--web th { color: var(--color-stone-500); font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
.sp-report--web .sp-summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 12px; }
.sp-report--web .sp-summary-card { border: 1px solid var(--color-stone-200); border-radius: var(--radius-xl); padding: 14px; }
.sp-report--web .sp-summary-card__label { display: block; color: var(--color-stone-500); font-size: 12px; }
.sp-report--web .sp-summary-card strong { display: block; font-weight: 600; }
.sp-report--web .sp-list { padding-left: 22px; }
.sp-report--web .sp-muted { color: var(--color-stone-500); }
.sp-report--web .sp-status { display: inline-block; border-radius: var(--radius-sm); padding: 2px 7px; font-size: 11px; white-space: nowrap; font-weight: 600; }
.sp-report--web .sp-status--pass { background: var(--color-emerald-100); color: var(--color-emerald-900); }
.sp-report--web .sp-status--warn { background: #fff2d8; color: #9b5d00; }
.sp-report--web .sp-status--fail { background: #fbe7e5; color: #a9342f; }
.sp-report--web .sp-finding { border-left: 3px solid var(--color-stone-200); padding-left: 14px; margin: 18px 0; }
.sp-report--web .sp-finding--acknowledged { border-color: var(--color-emerald-800); }
.sp-report--web .sp-photo-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
.sp-report--web .sp-photo { margin: 0; }
.sp-report--web .sp-photo img { display: block; width: 100%; height: auto; max-height: 400px; object-fit: cover; }
.sp-report--web .sp-photo figcaption { color: var(--color-stone-500); font-size: 12px; margin-top: 7px; }
`;
